import { CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { html } from 'lit/static-html.js';
import '../sic-button/sic-button';
import '../sic-container/sic-container';
import '../sic-heading/sic-heading';
// import typographyStyles from '../sic-global/sic-global';
import componentStyles from './sic-generic-countdown.css.lit';
import defaultVars from './css-variables/vars-default.css.lit';

@customElement('sic-generic-countdown')
export class SicGenericCountdown extends OutlineElement {

  static styles: CSSResultGroup = [defaultVars, componentStyles];

  @property({ type: String, attribute: 'target-date' })
  targetDate: string | undefined;

  @property({ type: String, attribute: 'extended-date' })
  extendedDate: string | undefined;

  @property({ type: String, attribute: 'text-before' })
  textBefore: string = "";

  @property({ type: String, attribute: 'text-after' })
  textAfter: string = "";

  @property({ type: String, attribute: 'text-extend-before' })
  textExtendedBefore: string = "";

  @property({ type: String, attribute: 'text-extend-after' })
  textExtendedAfter: string = "";

  @state()
  private daysRemaining: number | null = null;

  @state()
  private isExtended: boolean = false;

  connectedCallback(): void {
    super.connectedCallback();
    // this.calculateDaysRemainingInLaTime();
    // this.setSlotContent();
  }

  firstUpdated(): void {
   this.calculateDaysRemainingInLaTime();
  }

  isDstInEffectInLA(date: Date = new Date()): boolean {
    // Get the current year
    const year = date.getFullYear();

    // Helper: find nth weekday of a month (0=Sun..6=Sat)
    const nthWeekdayOfMonth = (month: number, weekday: number, n: number): Date => {
      const firstOfMonth = new Date(year, month - 1, 1);
      const firstWeekday = firstOfMonth.getDay();
      let day = 1 + ((weekday - firstWeekday + 7) % 7);
      day += (n - 1) * 7;
      return new Date(year, month - 1, day);
    };

    // DST starts: 2nd Sunday in March at 2:00 AM
    const dstStart = nthWeekdayOfMonth(3, 0, 2);
    dstStart.setHours(2, 0, 0, 0);

    // DST ends: 1st Sunday in November at 2:00 AM
    const dstEnd = nthWeekdayOfMonth(11, 0, 1);
    dstEnd.setHours(2, 0, 0, 0);

    // Check if current date is within DST period
    return date >= dstStart && date < dstEnd;
  }

  /**
   * Calculates days remaining to LA midnight (final-day-inclusive).
   *
   * @remarks
   * Uses site time zone (Los Angeles) with DST-aware offsets.
   * Converts LA midnight to UTC for consistent comparison.
   *
   * @see /admin/config/regional/settings
   * @returns {void}
   */
  calculateDaysRemainingInLaTime(): void {
    // Constants: 1 day in ms.
    const MS_PER_DAY = 24 * 60 * 60 * 1000;

    // Current UTC time.
    const nowUtc = new Date();

    // Helper: get the UTC timestamp that corresponds to LA midnight at the start of a date.
    const toLaMidnightUtc = (dateStr: string): number => {
      // Expects ISO date string: YYYY-MM-DD
      // Use noon UTC to avoid edge/timezone issues when determining the calendar day.
      const probe = new Date(`${dateStr}T12:00:00Z`);
      const offset = this.isDstInEffectInLA(probe) ? '-07:00' : '-08:00';
      // Creates a Date at local LA midnight using the chosen offset, then reads its UTC milliseconds.
      return new Date(`${dateStr}T00:00:00${offset}`).getTime();
    };

    // Reset state
    this.daysRemaining = null;
    this.isExtended = false;

    // Helper: ceil in whole days (we measure to end-of-day to be inclusive of the final day).
    const ceilDays = (diffMs: number): number =>
      Math.max(0, Math.ceil(diffMs / MS_PER_DAY));

    const computeToEndOfDayDays = (dateStr: string): number => {
      // End-of-day is next day's midnight in LA.
      const start = toLaMidnightUtc(dateStr);
      const endOfDay = start + MS_PER_DAY;
      const diffMs = endOfDay - nowUtc.getTime();
      return ceilDays(diffMs);
    };

    if (this.targetDate) {
      const daysToTargetEod = computeToEndOfDayDays(this.targetDate);

      if (daysToTargetEod > 0) {
        // Target day has not fully ended yet → still within inclusive window.
        this.daysRemaining = daysToTargetEod;
        this.isExtended = false;
        return;
      }
    }

    if (this.extendedDate) {
      const daysToExtendedEod = computeToEndOfDayDays(this.extendedDate);

      if (daysToExtendedEod > 0) {
        // Target day ended; but extended window still active.
        this.daysRemaining = daysToExtendedEod;
        this.isExtended = true;
        return;
      }
    }

    // If we get here, both target & extended windows have ended.
    this.daysRemaining = 0;
    this.isExtended = false;
  }


  render(): TemplateResult {

    if (!this.targetDate) {
      return html``;
    }

    const displayTextBefore = this.isExtended ? this.textExtendedBefore : this.textBefore;
    const displayTextAfter = this.isExtended ? this.textExtendedAfter : this.textAfter;
    return html`
        <sic-heading level-size="1" level-style="bold" class="headline-07">
        ${displayTextBefore} &mdash;
        <span class="days-left">
          less than ${this.daysRemaining ?? 0} day${(this.daysRemaining ?? 0) > 1 ? 's' : ''}
        </span>
           ${displayTextAfter}
        </sic-heading>
        <slot name="text-byline" style="font-size: 20px; margin-bottom: var(--spacing-04);"></slot>
        <slot name="cta"></slot>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-generic-countdown': SicGenericCountdown;
  }
}
